import * as React from "react";
import {
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
  Link,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import ReactFileReader from "react-file-reader";
import { notification } from "antd";
import { putRequest } from "../../core/network";
import { uploadFile } from "../../core/aws";
import endpoints from "../../constants/endPoints.json";
import { marketingFonts } from "../../app/components/tickitMarketingTheme";
import { getBrandingColors, brandedFieldSx, brandedTextSx } from "./brandingUtils";
import { cardSx, layout } from "./templateLayout";

export default function RegistrationPaymentStep({
  requestId,
  email,
  paymentMethod,
  paymentAddress = [],
  branding = {},
  onComplete,
}) {
  const [receipt, setReceipt] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [done, setDone] = React.useState(false);

  const { primary: primaryColor, accent: accentColor } = getBrandingColors(branding);
  const fieldSx = brandedFieldSx(accentColor);
  const bodySx = { ...brandedTextSx(accentColor), opacity: 0.9 };
  const headingSx = {
    fontFamily: marketingFonts.display,
    fontWeight: 700,
    mb: 2,
    ...brandedTextSx(accentColor),
  };

  const handleInstaPaySubmit = async (ev) => {
    ev.preventDefault();
    if (!receipt) {
      notification.error({ message: "Please upload your payment receipt" });
      return;
    }
    setSubmitting(true);
    try {
      const data = new FormData(ev.currentTarget);
      const receiptUrl = await uploadFile(receipt);
      await putRequest(
        {
          requestId,
          email,
          receipt: receiptUrl,
          instapayUser: data.get("instapayUser"),
        },
        null,
        null,
        null,
        endpoints.guest.registrationRequestPay
      );
      setDone(true);
      onComplete?.();
    } catch (err) {
      notification.error({
        message: "Payment submission failed",
        description: err?.response?.data?.message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <Paper
        sx={{
          ...cardSx(branding),
          p: layout.cardPadding,
          textAlign: "center",
          maxWidth: 480,
          width: "100%",
          mx: "auto",
        }}
      >
        <Typography sx={{ fontWeight: 700, mb: 1, ...brandedTextSx(accentColor) }}>Payment submitted</Typography>
        <Typography sx={bodySx}>
          Your InstaPay receipt was sent. You will receive your ticket by email once payment is confirmed.
        </Typography>
      </Paper>
    );
  }

  if (paymentMethod === "InstaPay") {
    return (
      <Paper
        sx={{
          ...cardSx(branding),
          p: layout.cardPadding,
          maxWidth: 520,
          width: "100%",
          mx: "auto",
        }}
      >
        <Typography sx={headingSx}>Complete payment</Typography>
        <Typography sx={{ mb: 2, ...bodySx }}>
          Transfer to: <strong>{(paymentAddress || []).join(", ")}</strong>
        </Typography>
        <Box component="form" onSubmit={handleInstaPaySubmit}>
          <Stack spacing={2}>
            <TextField
              required
              fullWidth
              label="Your InstaPay username"
              name="instapayUser"
              sx={fieldSx}
            />
            <ReactFileReader
              multipleFiles={false}
              handleFiles={(files) => setReceipt(files[0])}
              fileTypes={[".jpeg", ".jpg", ".png", ".pdf"]}
            >
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              fullWidth
              sx={{
                color: accentColor,
                borderColor: `${accentColor}66`,
                minHeight: 48,
                "&:hover": { borderColor: accentColor, bgcolor: `${accentColor}0a` },
              }}
            >
                {receipt ? receipt.name : "Upload payment receipt"}
              </Button>
            </ReactFileReader>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              fullWidth
              sx={{
                bgcolor: primaryColor,
                color: "#fff",
                minHeight: 48,
                "&:hover": { bgcolor: primaryColor, filter: "brightness(0.92)" },
              }}
            >
              {submitting ? "Submitting…" : "Submit payment"}
            </Button>
          </Stack>
        </Box>
      </Paper>
    );
  }

  if (paymentMethod === "Cash") {
    return (
      <Paper
        sx={{
          ...cardSx(branding),
          p: layout.cardPadding,
          textAlign: "center",
          maxWidth: 480,
          width: "100%",
          mx: "auto",
        }}
      >
        <Typography sx={headingSx}>Complete payment</Typography>
        <Typography sx={{ ...bodySx, mb: 2 }}>
          Your registration was approved. Call one of these numbers to pay and receive your ticket:
        </Typography>
        <Stack spacing={1}>
          {(paymentAddress || []).map((phone) => (
            <Link key={phone} href={`tel:${phone}`} sx={{ fontSize: "1.1rem", color: accentColor }}>
              {phone}
            </Link>
          ))}
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        ...cardSx(branding),
        p: layout.cardPadding,
        textAlign: "center",
        maxWidth: 480,
        width: "100%",
        mx: "auto",
      }}
    >
      <Typography sx={headingSx}>Complete payment</Typography>
      <Typography sx={bodySx}>
        Your registration was approved. Check your email for payment instructions, or sign in to your
        Tickit account under My Requests to pay online.
      </Typography>
    </Paper>
  );
}
